// src/components/layout/views/TraceabilityView.jsx

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiZoomIn, FiZoomOut, FiRotateCcw, FiMaximize, FiMinimize,
  FiSearch, FiX, FiDownload, FiActivity, FiTarget, FiLayers,
  FiBox, FiFolder, FiCpu, FiDatabase, FiServer, FiShield,
  FiMonitor, FiGitBranch, FiChevronRight, FiChevronDown,
  FiChevronUp, FiArrowRight, FiArrowLeft, FiCheckCircle,
  FiAlertTriangle, FiInfo, FiFilter, FiEye, FiEyeOff,
  FiHome, FiBriefcase, FiPackage, FiTag, FiHash,
  FiDollarSign, FiTrendingUp, FiZap, FiGrid, FiList
} from 'react-icons/fi';
import * as d3 from 'd3';
import { useSolution } from '../../../context/SolutionContext';
import { UNIVERSAL_INDUSTRIES } from '../../../data/universalIndustries';
import { AI_ONTOLOGY } from '../../../data/aiOntology';

// ============================================================
// TRACEABILITY LEVELS
// ============================================================

const TRACE_LEVELS = {
  REQUIREMENT: { id: 'requirement', label: 'Requirement', icon: FiTarget, color: '#ef4444' },
  INDUSTRY: { id: 'industry', label: 'Industry', icon: FiBriefcase, color: '#3b82f6' },
  USE_CASE: { id: 'useCase', label: 'Use Case', icon: FiZap, color: '#8b5cf6' },
  SUB_INDUSTRY: { id: 'subIndustry', label: 'Sub-Industry', icon: FiFolder, color: '#f59e0b' },
  LAYER: { id: 'layer', label: 'Layer', icon: FiLayers, color: '#10b981' },
  SUB_LAYER: { id: 'subLayer', label: 'Sub-Layer', icon: FiBox, color: '#06b6d4' },
  COMPONENT: { id: 'component', label: 'Component', icon: FiCpu, color: '#f97316' }
};

// ============================================================
// BUILD COMPLETE TRACEABILITY CHAIN
// ============================================================

function buildTraceabilityChain(state) {
  const chain = {
    requirements: [],
    industries: [],
    useCases: [],
    subIndustries: [],
    layers: [],
    subLayers: [],
    components: [],
    links: []
  };

  // Get requirements from state
  const requirements = state.autoArchitectRequirements || {};
  const selectedComponents = state.selectedComponents || [];
  const approaches = state.selectedApproaches || [];

  // Build Requirement nodes
  const requirementNodes = [
    { id: 'req-1', name: `Industry: ${requirements.industry || 'Not Selected'}`, type: 'requirement', level: 0 },
    { id: 'req-2', name: `Problems: ${(requirements.problems || []).length} selected`, type: 'requirement', level: 0 },
    { id: 'req-3', name: `Budget: ${requirements.budget || 'Not Set'}`, type: 'requirement', level: 0 },
    { id: 'req-4', name: `Scale: ${requirements.scale || 'Not Set'}`, type: 'requirement', level: 0 },
    { id: 'req-5', name: `Compliance: ${requirements.compliance || 'Not Set'}`, type: 'requirement', level: 0 }
  ];
  chain.requirements = requirementNodes;

  // Build Industry nodes
  const industryNodes = [];
  if (requirements.industry && UNIVERSAL_INDUSTRIES[requirements.industry]) {
    const ind = UNIVERSAL_INDUSTRIES[requirements.industry];
    industryNodes.push({
      id: `ind-${ind.id}`,
      name: ind.name,
      icon: ind.icon,
      type: 'industry',
      level: 1
    });
    
    // Link Requirements to Industry
    chain.links.push({ source: 'req-1', target: `ind-${ind.id}`, type: 'requirement-industry' });
    chain.links.push({ source: 'req-2', target: `ind-${ind.id}`, type: 'requirement-industry' });
  }
  chain.industries = industryNodes;

  // Build Use Case nodes from problems
  const useCaseNodes = [];
  const problemList = requirements.problems || [];
  
  problemList.forEach((problemId, index) => {
    const industryData = UNIVERSAL_INDUSTRIES[requirements.industry];
    const problem = industryData?.problems?.find(p => p.id === problemId);
    
    if (problem) {
      const ucNode = {
        id: `uc-${problem.id}`,
        name: problem.name,
        icon: problem.icon || '🎯',
        type: 'useCase',
        level: 2,
        problemId: problem.id,
        description: problem.description || '',
        useCases: problem.useCases || []
      };
      useCaseNodes.push(ucNode);
      
      // Link Industry to Use Case
      if (industryNodes.length > 0) {
        chain.links.push({ 
          source: `ind-${industryNodes[0].id.split('-')[1]}`, 
          target: `uc-${problem.id}`, 
          type: 'industry-usecase' 
        });
      }
    }
  });
  chain.useCases = useCaseNodes;

  // Build Sub-Industry nodes
  const subIndustryNodes = [];
  if (requirements.subIndustry) {
    subIndustryNodes.push({
      id: `si-${requirements.subIndustry}`,
      name: requirements.subIndustry,
      type: 'subIndustry',
      level: 3
    });
    
    // Link Industry to Sub-Industry
    if (industryNodes.length > 0) {
      chain.links.push({ 
        source: `ind-${industryNodes[0].id.split('-')[1]}`, 
        target: `si-${requirements.subIndustry}`, 
        type: 'industry-subindustry' 
      });
    }
    
    // Link Use Cases to Sub-Industry
    useCaseNodes.forEach(uc => {
      chain.links.push({ 
        source: uc.id, 
        target: `si-${requirements.subIndustry}`, 
        type: 'usecase-subindustry' 
      });
    });
  }
  chain.subIndustries = subIndustryNodes;

  // Build Layer nodes from selected components
  const layerMap = new Map();
  const subLayerMap = new Map();
  
  selectedComponents.forEach((comp, index) => {
    const layerName = comp.layerName || comp.layer || 'Unknown Layer';
    const subLayerName = comp.subLayerName || comp.subLayer || 'Unknown Sub-Layer';
    
    // Create Layer node if not exists
    if (!layerMap.has(layerName)) {
      const layerNode = {
        id: `layer-${layerName}`,
        name: layerName,
        type: 'layer',
        level: 4
      };
      layerMap.set(layerName, layerNode);
      chain.layers.push(layerNode);
      
      // Link Use Cases to Layers
      useCaseNodes.forEach(uc => {
        chain.links.push({ 
          source: uc.id, 
          target: `layer-${layerName}`, 
          type: 'usecase-layer' 
        });
      });
      
      // Link Sub-Industry to Layers
      subIndustryNodes.forEach(si => {
        chain.links.push({ 
          source: si.id, 
          target: `layer-${layerName}`, 
          type: 'subindustry-layer' 
        });
      });
    }
    
    // Create Sub-Layer node if not exists
    const subLayerKey = `${layerName}::${subLayerName}`;
    if (!subLayerMap.has(subLayerKey)) {
      const subLayerNode = {
        id: `sublayer-${subLayerKey}`,
        name: subLayerName,
        type: 'subLayer',
        level: 5,
        layerName
      };
      subLayerMap.set(subLayerKey, subLayerNode);
      chain.subLayers.push(subLayerNode);
      
      // Link Layer to Sub-Layer
      chain.links.push({ 
        source: `layer-${layerName}`, 
        target: `sublayer-${subLayerKey}`, 
        type: 'layer-sublayer' 
      });
    }
    
    // Create Component node
    const compNode = {
      id: `comp-${comp.id || index}`,
      name: comp.name || 'Unknown Component',
      icon: comp.icon || '🔧',
      type: 'component',
      level: 6,
      layerName,
      subLayerName,
      cost: comp.cost,
      alternativesCount: comp.alternativesCount || 0
    };
    chain.components.push(compNode);
    
    // Link Sub-Layer to Component
    chain.links.push({ 
      source: `sublayer-${subLayerKey}`, 
      target: `comp-${comp.id || index}`, 
      type: 'sublayer-component' 
    });
  });

  return chain;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

const TraceabilityView = () => {
  const { state } = useSolution();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [highlightedPath, setHighlightedPath] = useState(null);
  const [showDetails, setShowDetails] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [viewMode, setViewMode] = useState('flow'); // 'flow' | 'hierarchy' | 'matrix'
  const [searchTerm, setSearchTerm] = useState('');
  const zoomBehaviorRef = useRef(null);

  // Build traceability data
  const traceabilityData = useMemo(() => {
    return buildTraceabilityChain(state);
  }, [state]);

  // D3 Visualization
  useEffect(() => {
    if (!svgRef.current) return;
    
    const container = svgRef.current;
    const width = container.clientWidth || 1200;
    const height = container.clientHeight || 800;
    
    // Clear previous
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    
    // Create SVG
    const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svgEl.setAttribute('width', '100%');
    svgEl.setAttribute('height', '100%');
    svgEl.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svgEl.style.cursor = 'grab';
    container.appendChild(svgEl);
    
    const svg = d3.select(svgEl);
    
    // Add defs for arrows and gradients
    const defs = svg.append('defs');
    
    // Arrow markers
    ['red', 'blue', 'purple', 'orange', 'green', 'cyan', 'amber'].forEach(color => {
      defs.append('marker')
        .attr('id', `arrow-${color}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 8)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .style('fill', color);
    });
    
    // Glow filter
    const glowFilter = defs.append('filter')
      .attr('id', 'glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    
    glowFilter.append('feGaussianBlur')
      .attr('stdDeviation', '4')
      .attr('result', 'blur');
    
    const feMerge = glowFilter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'blur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    
    const g = svg.append('g').attr('class', 'viewport');
    
    // Build all nodes with positions
    const allNodes = [];
    const nodeMap = new Map();
    
    // Position levels vertically
    const levelWidth = width / 7; // 7 levels
    const nodeSpacing = 80;
    
    // Helper to add nodes at level
    const addNodesAtLevel = (nodes, levelIndex) => {
      const levelX = levelWidth * levelIndex + levelWidth / 2;
      const totalHeight = nodes.length * nodeSpacing;
      const startY = (height - totalHeight) / 2;
      
      nodes.forEach((node, index) => {
        const nodeWithPos = {
          ...node,
          x: levelX,
          y: startY + index * nodeSpacing + nodeSpacing / 2
        };
        allNodes.push(nodeWithPos);
        nodeMap.set(node.id, nodeWithPos);
      });
    };
    
    // Add all levels
    addNodesAtLevel(traceabilityData.requirements, 0);
    addNodesAtLevel(traceabilityData.industries, 1);
    addNodesAtLevel(traceabilityData.useCases, 2);
    addNodesAtLevel(traceabilityData.subIndustries, 3);
    addNodesAtLevel(traceabilityData.layers, 4);
    addNodesAtLevel(traceabilityData.subLayers, 5);
    addNodesAtLevel(traceabilityData.components, 6);
    
    // Draw links
    const linkSelection = g.selectAll('.link')
      .data(traceabilityData.links)
      .enter()
      .append('line')
      .attr('class', 'link')
      .style('stroke', d => {
        const sourceNode = nodeMap.get(d.source);
        const sourceType = sourceNode?.type;
        return TRACE_LEVELS[Object.keys(TRACE_LEVELS).find(k => TRACE_LEVELS[k].id === sourceType)]?.color || '#94a3b8';
      })
      .style('stroke-width', 2)
      .style('stroke-opacity', 0.6)
      .style('stroke-dasharray', '4,4')
      .attr('marker-end', d => {
        const sourceNode = nodeMap.get(d.source);
        const sourceType = sourceNode?.type;
        const colorKey = sourceType === 'requirement' ? 'red' : 
                         sourceType === 'industry' ? 'blue' : 
                         sourceType === 'useCase' ? 'purple' : 
                         sourceType === 'subIndustry' ? 'orange' : 
                         sourceType === 'layer' ? 'green' : 
                         sourceType === 'subLayer' ? 'cyan' : 'amber';
        return `url(#arrow-${colorKey})`;
      });
    
    // Update link positions
    linkSelection
      .attr('x1', d => nodeMap.get(d.source)?.x || 0)
      .attr('y1', d => nodeMap.get(d.source)?.y || 0)
      .attr('x2', d => nodeMap.get(d.target)?.x || 0)
      .attr('y2', d => nodeMap.get(d.target)?.y || 0);
    
    // Draw nodes
    const nodeSelection = g.selectAll('.node')
      .data(allNodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x},${d.y})`)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        handleNodeClick(d);
      })
      .on('mouseenter', (event, d) => {
        setHoveredNode(d);
        highlightConnectedNodes(d, allNodes, traceabilityData.links);
      })
      .on('mouseleave', () => {
        setHoveredNode(null);
        setHighlightedPath(null);
      });
    
    // Node circles
    nodeSelection.append('circle')
      .attr('r', d => getNodeRadius(d.type))
      .style('fill', d => getNodeColor(d.type))
      .style('stroke', '#fff')
      .style('stroke-width', 2.5)
      .style('filter', d => {
        if (highlightedPath && highlightedPath.includes(d.id)) return 'url(#glow)';
        return 'none';
      })
      .style('opacity', d => {
        if (highlightedPath && !highlightedPath.includes(d.id)) return 0.3;
        return 1;
      });
    
    // Node icons (emoji)
    nodeSelection.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .style('font-size', d => getNodeRadius(d.type) * 0.8)
      .text(d => {
        const levelInfo = TRACE_LEVELS[Object.keys(TRACE_LEVELS).find(k => TRACE_LEVELS[k].id === d.type)];
        return d.icon || levelInfo?.icon ? (d.icon || '📦') : '📦';
      });
    
    // Node labels
    nodeSelection.append('text')
      .attr('dy', d => getNodeRadius(d.type) + 15)
      .attr('text-anchor', 'middle')
      .style('font-size', d => d.type === 'component' ? '9px' : d.type === 'subLayer' ? '10px' : '11px')
      .style('fill', '#1e293b')
      .style('font-weight', d => d.type === 'industry' || d.type === 'useCase' ? 'bold' : 'normal')
      .style('pointer-events', 'none')
      .text(d => d.name.length > 25 ? d.name.substring(0, 25) + '...' : d.name);
    
    // Level labels at top
    Object.values(TRACE_LEVELS).forEach((level, index) => {
      const levelX = levelWidth * index + levelWidth / 2;
      g.append('text')
        .attr('x', levelX)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('fill', level.color)
        .text(level.label);
    });
    
    // Zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 5])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        setZoomLevel(event.transform.k);
      });
    
    zoomBehaviorRef.current = zoom;
    svg.call(zoom);
    
    // Drag for panning
    svg.call(d3.drag()
      .on('start', function() { d3.select(this).style('cursor', 'grabbing'); })
      .on('end', function() { d3.select(this).style('cursor', 'grab'); })
    );
    
  }, [traceabilityData, highlightedPath]);

  // ==================== HIGHLIGHT CONNECTED NODES ====================
  
  const highlightConnectedNodes = useCallback((node, allNodes, links) => {
    const connectedIds = new Set();
    connectedIds.add(node.id);
    
    // BFS to find all connected nodes
    const queue = [node.id];
    const visited = new Set([node.id]);
    
    while (queue.length > 0) {
      const currentId = queue.shift();
      
      links.forEach(link => {
        if (link.source === currentId && !visited.has(link.target)) {
          visited.add(link.target);
          connectedIds.add(link.target);
          queue.push(link.target);
        }
        if (link.target === currentId && !visited.has(link.source)) {
          visited.add(link.source);
          connectedIds.add(link.source);
          queue.push(link.source);
        }
      });
    }
    
    setHighlightedPath(Array.from(connectedIds));
  }, []);

  // ==================== HANDLERS ====================
  
  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
  }, []);

  const getNodeRadius = (type) => {
    switch (type) {
      case 'requirement': return 18;
      case 'industry': return 22;
      case 'useCase': return 20;
      case 'subIndustry': return 18;
      case 'layer': return 20;
      case 'subLayer': return 16;
      case 'component': return 14;
      default: return 16;
    }
  };

  const getNodeColor = (type) => {
    const levelInfo = TRACE_LEVELS[Object.keys(TRACE_LEVELS).find(k => TRACE_LEVELS[k].id === type)];
    return levelInfo?.color || '#94a3b8';
  };

  const resetView = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current) {
      d3.select(svgRef.current.querySelector('svg'))
        .transition()
        .duration(500)
        .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
    }
  }, []);

  const zoomIn = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current) {
      d3.select(svgRef.current.querySelector('svg'))
        .transition()
        .duration(300)
        .call(zoomBehaviorRef.current.scaleBy, 1.5);
    }
  }, []);

  const zoomOut = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current) {
      d3.select(svgRef.current.querySelector('svg'))
        .transition()
        .duration(300)
        .call(zoomBehaviorRef.current.scaleBy, 0.75);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);

  // ==================== RENDER ====================

  return (
    <div 
      ref={containerRef}
      className={`h-full overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 ${isFullscreen ? 'bg-white' : ''}`}
      style={isFullscreen ? { position: 'fixed', inset: 0, zIndex: 9999 } : {}}
    >
      {/* HEADER */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-50 rounded-lg">
            <FiActivity className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">End-to-End Traceability</h2>
            <p className="text-xs text-gray-500">
              Requirement → Industry → Use Case → Sub-Industry → Layer → Sub-Layer → Component
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button onClick={zoomOut} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200" title="Zoom Out">
            <FiZoomOut className="w-4 h-4 text-gray-600" />
          </button>
          <button onClick={zoomIn} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200" title="Zoom In">
            <FiZoomIn className="w-4 h-4 text-gray-600" />
          </button>
          <button onClick={resetView} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200" title="Reset View">
            <FiRotateCcw className="w-4 h-4 text-gray-600" />
          </button>
          <button onClick={toggleFullscreen} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200" title="Fullscreen">
            {isFullscreen ? <FiMinimize className="w-4 h-4 text-gray-600" /> : <FiMaximize className="w-4 h-4 text-gray-600" />}
          </button>
        </div>
      </div>
      
      {/* LEGEND */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-4 flex-wrap">
        {Object.values(TRACE_LEVELS).map(level => {
          const Icon = level.icon;
          return (
            <div key={level.id} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: level.color }} />
              <span className="text-xs text-gray-600">{level.label}</span>
            </div>
          );
        })}
      </div>
      
      {/* SVG CONTAINER */}
      <div ref={svgRef} className="flex-1" style={{ height: 'calc(100% - 120px)' }} />
      
      {/* DETAILS PANEL */}
      <AnimatePresence>
        {selectedNode && (
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            className="absolute top-20 right-4 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 z-20"
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900">{selectedNode.name}</h3>
                <span className="text-xs text-gray-400 capitalize">{selectedNode.type}</span>
              </div>
              <button onClick={() => setSelectedNode(null)} className="p-1 hover:bg-gray-100 rounded">
                <FiX className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {selectedNode.type === 'useCase' && selectedNode.description && (
                <p className="text-xs text-gray-600">{selectedNode.description}</p>
              )}
              {selectedNode.type === 'component' && (
                <>
                  <div className="text-xs text-gray-500">
                    Layer: <span className="font-semibold">{selectedNode.layerName}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Sub-Layer: <span className="font-semibold">{selectedNode.subLayerName}</span>
                  </div>
                  {selectedNode.cost && (
                    <div className="text-xs text-gray-500">
                      Cost: <span className="font-semibold">${selectedNode.cost.low}-${selectedNode.cost.high}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TraceabilityView;